import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchAbsentEmployee, fetchAbsentEmployeeBasedLoc } from "../../actions/AbsentEmployeeAction";
import homeImage from "../../assets/DTS2.png";

// ------------ Styles ------------
const PageContainer = styled(Box)(() => ({
  maxWidth: "1000px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
}));

const StickyCardsContainer = styled(Box)(() => ({
  position: "sticky",
  top: 0,
  backgroundColor: "#ffffff",
  zIndex: 100,
  paddingTop: "10px",
  paddingBottom: "10px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const ScrollableContent = styled(Box)(() => ({
  overflowY: "auto",
  maxHeight: "calc(100vh - 150px)",
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  maxHeight: "700px",
  overflowY: "auto",
  overflowX: "auto",
}));

const StyledTableCell = styled(TableCell)(() => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "14px 10px",
  fontSize: "14px",
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "&.MuiTableCell-head": {
    backgroundColor: "#154C79",
    color: "#FFFFFF",
    fontWeight: 600,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.09)",
  },
}));

export default function AbsentEmployee() {
  const dispatch = useDispatch();

  const { loading, data, error, empLoading, empData, empError } = useSelector(
    (state) => state.absentEmployee
  );

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("full"); // "full" | "location"

  // Format as YYYY-MM-DD
  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  // Compute yesterday from selected date
  const getYesterday = (dateObj) => {
    const d = new Date(dateObj);
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  };

  const formatTime = (dateObj) => {
    const d = new Date(dateObj);
    return d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  useEffect(() => {
    const sdate = formatDate(selectedDate);
    const yesterday = getYesterday(selectedDate);

    if (viewMode === "full") {
      dispatch(fetchAbsentEmployee(sdate, yesterday));
    }

    if (viewMode === "location") {
      dispatch(fetchAbsentEmployeeBasedLoc(sdate, yesterday));
    }
  }, [dispatch, selectedDate, viewMode]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
  };

  const totalEmployees =
    viewMode === "full"
      ? data?.length || 0
      : empData?.reduce((sum, item) => sum + (Number(item.Count) || 0), 0) || 0;

  const isLoading = viewMode === "full" ? loading : empLoading;
  const activeError = viewMode === "full" ? error : empError;

  // ================= PDF EXPORT =================
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const generatedAt = new Date();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(homeImage, "PNG", 0, 0, 210, 65);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121);
    const title =
      viewMode === "full"
        ? "Employee Absent Report"
        : "Location-Based Absent Summary";
    doc.text(title, pageWidth / 2, 75, { align: "center" });

    doc.setDrawColor(21, 76, 121);
    doc.setLineWidth(0.5);
    doc.line(15, 82, pageWidth - 15, 82);

    doc.setFontSize(12);
    doc.setTextColor(60, 60, 60);

    doc.setFont("helvetica", "bold");
    doc.text("Date - ", 16, 88);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(selectedDate), 28, 88);

    doc.setFont("helvetica", "bold");
    doc.text("Time - ", pageWidth / 2 - 23, 88);
    doc.setFont("helvetica", "normal");
    doc.text(formatTime(generatedAt), pageWidth / 2 - 8, 88);

    doc.setFont("helvetica", "bold");
    doc.text("Total Employees - ", pageWidth - 63, 88);
    doc.setFont("helvetica", "normal");
    doc.text(totalEmployees.toString(), pageWidth - 25, 88);

    const footerFn = (d) => {
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.setFont("helvetica", "italic");
      doc.text(
        `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
      doc.text(
        `Page ${d.pageNumber}`,
        pageWidth - 20,
        doc.internal.pageSize.getHeight() - 10
      );
    };

    if (viewMode === "full") {
      autoTable(doc, {
        startY: 95,
        head: [["Location", "Work Category", "Service No", "Name", "Designation", "Mobile"]],
        body: data?.map((item) => [
          item.Location || "",
          item.WorkCategory || "",
          item.ServiceNo || "",
          item.Name || "",
          item.Designation || "",
          item.Mobile || "",
        ]),
        styles: { fontSize: 9, cellPadding: 4, lineColor: [220, 220, 220], lineWidth: 0.1 },
        headStyles: { fillColor: [21, 76, 121], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9, halign: "center", cellPadding: 5 },
        alternateRowStyles: { fillColor: [248, 249, 252] },
        columnStyles: {
          0: { cellWidth: 20, halign: "center" },
          1: { cellWidth: 30, halign: "center" },
          2: { cellWidth: 25, halign: "center" },
          3: { cellWidth: 45, halign: "left" },
          4: { cellWidth: 42, halign: "left" },
          5: { cellWidth: 28, halign: "center" },
        },
        margin: { left: 10, right: 10 },
        theme: "grid",
        didDrawPage: footerFn,
      });
    } else {
      autoTable(doc, {
        startY: 95,
        head: [["Location", "Absent Count"]],
        body: empData?.map((item) => [item.Location || "", item.Count || ""]),
        styles: { fontSize: 12, cellPadding: 5, lineColor: [220, 220, 220], lineWidth: 0.1 },
        headStyles: { fillColor: [21, 76, 121], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 10, halign: "center", cellPadding: 6 },
        alternateRowStyles: { fillColor: [248, 249, 252] },
        columnStyles: {
          0: { cellWidth: 80, halign: "center" },
          1: { cellWidth: 60, halign: "center" },
        },
        margin: { left: 30, right: 30 },
        theme: "grid",
        didDrawPage: footerFn,
      });
    }

    const suffix = viewMode === "full" ? "FullDetails" : "LocationSummary";
    doc.save(`Employee_Absent_${suffix}_${formatDate(selectedDate)}.pdf`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#0e3c66",
            textAlign: "center",
            mb: 2,
          }}
        >
          Employee Absent
        </Typography>

        {!isLoading && (
          <StickyCardsContainer>
            {/* Row 1: Date | Total */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  startIcon={<CalendarTodayIcon />}
                  onClick={() => setOpenDatePicker(true)}
                  sx={{
                    width: "100%",
                    height: "56px",
                    borderRadius: "20px",
                    backgroundColor: "#142c54",
                    fontWeight: "bold",
                    textTransform: "none",
                  }}
                >
                  {formatDate(selectedDate)}
                </Button>
              </Grid>

              <Grid item xs={6}>
                <Box
                  sx={{
                    background: "#e8f0fa",
                    borderRadius: "20px",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontFamily: "'Poppins', sans-serif",
                    color: "#142c54",
                  }}
                >
                  Total: {totalEmployees}
                </Box>
              </Grid>
            </Grid>

            {/* Row 2: View Dropdown | Export PDF */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={8}>
                <FormControl fullWidth>
                  <InputLabel
                    sx={{
                      fontSize: { xs: "0.85rem", sm: "1rem" },
                      color: "#154C79",
                    }}
                  >
                    View Type
                  </InputLabel>
                  <Select
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                    label="View Type"
                    sx={{
                      fontSize: { xs: "0.85rem", sm: "1rem" },
                      borderRadius: "20px",
                      backgroundColor: "#F0F4F8",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      "& .MuiSelect-icon": { color: "#154C79" },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#154C79",
                      },
                    }}
                    MenuProps={{
                      PaperProps: { sx: { maxHeight: 250, overflowY: "auto" } },
                    }}
                  >
                    <MenuItem value="full" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                      Full Details
                    </MenuItem>
                    <MenuItem value="location" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                      Location Based Absent
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportPDF}
                  disabled={
                    isLoading ||
                    (viewMode === "full" ? !data?.length : !empData?.length)
                  }
                  sx={{
                    width: "100%",
                    height: "56px",
                    borderRadius: "20px",
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: { xs: "13px", sm: "16px" },
                    "&:hover": { backgroundColor: "#1b5e20" },
                    "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
                  }}
                >
                  Export PDF
                </Button>
              </Grid>
            </Grid>

            {openDatePicker && (
              <DatePicker
                open={openDatePicker}
                value={selectedDate}
                onChange={handleDateChange}
                onClose={() => setOpenDatePicker(false)}
                renderInput={(params) => (
                  <TextField {...params} sx={{ display: "none" }} />
                )}
              />
            )}
          </StickyCardsContainer>
        )}

        {/* Show backend error if API returns 500 */}
        {activeError && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: "12px",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Failed to load data. Please try again or contact support.
          </Alert>
        )}

        <ScrollableContent>
          {/* ===== FULL DETAILS TABLE ===== */}
          {viewMode === "full" && (
            <StyledTableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Location</StyledTableCell>
                    <StyledTableCell>Work Category</StyledTableCell>
                    <StyledTableCell>Service No</StyledTableCell>
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell>Designation</StyledTableCell>
                    <StyledTableCell>Mobile</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && data?.length > 0 ? (
                    data.map((item, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Location}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "left", minWidth: 60 }}>{item.WorkCategory}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.ServiceNo}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "left" }}>{item.Name}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "left" }}>{item.Designation}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Mobile}</StyledTableCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <TableRow>
                      <StyledTableCell colSpan={6}>
                        {loading ? "Loading..." : "No Records Found"}
                      </StyledTableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}

          {/* ===== LOCATION SUMMARY TABLE ===== */}
          {viewMode === "location" && (
            <StyledTableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Location</StyledTableCell>
                    <StyledTableCell>Absent Count</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!empLoading && empData?.length > 0 ? (
                    empData.map((item, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell sx={{ textAlign: "center" }}>
                          {item.Location}
                        </StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>
                            {item.Count}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <TableRow>
                      <StyledTableCell colSpan={2}>
                        {empLoading ? "Loading..." : "No Records Found"}
                      </StyledTableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </ScrollableContent>
      </PageContainer>
    </LocalizationProvider>
  );
}